/**
 * WCAG 3.1.1 / 3.1.2 (Level A / AA) & scan finding F71 — automatic lang
 * tagging for text written in a non-page script.
 *
 * Problem: review apps (Judge.me, Bazaarvoice, …) and merchant-configured
 * content inject user-generated text at runtime, after the page is served.
 * When such text uses another alphabet (e.g. a Cyrillic review on a
 * lang="en" page), screen readers apply English pronunciation rules to it
 * and produce unintelligible speech (F71).
 *
 * How it works (fully content-driven — nothing about specific apps,
 * products, reviews or pages is hardcoded):
 *  - Each element's own (direct-child) text is classified letter by letter
 *    into Unicode scripts (Cyrillic, Greek, Han, Kana, Hangul, Arabic,
 *    Hebrew, Thai, Devanagari, …).
 *  - When one foreign script makes up the STRICT MAJORITY of the element's
 *    letters AND differs from the script implied by <html lang>, the best
 *    BCP 47 tag for that script is written onto the element (lang="ru",
 *    lang="uk", lang="ja", lang="el", …). The majority rule keeps mixed
 *    snippets (e.g. an English sentence quoting one foreign word) untouched,
 *    so the surrounding language keeps governing pronunciation.
 *  - Script → language resolution is character-based: any Kana → ja, any
 *    Hangul → ko, lone Han → zh, and Cyrillic is refined by unique letters
 *    (ў → be; ї/ґ/є/і → uk; ђ/ћ/џ/љ/њ → sr; ѓ/ќ → mk; else ru).
 *  - Same-alphabet (Latin) languages — a Spanish review on an English page,
 *    the other half of 3.1.2 — cannot be told apart by scripts, so they use
 *    word statistics instead: function-word lists for es fr pt it de nl sv
 *    da no fi pl cs sk hu ro tr id vi tl, plus near-unique signature
 *    letters (ñ ¿ ¡ → es; ã õ → pt; ß ü → de; ą ć ę ł ń ś ź ż → pl;
 *    ğ ş ı → tr; ș ț ţ → ro; ř ů ě → cs; ľ ĺ → sk; ő ű → hu; the
 *    Vietnamese stacked-vowel block → vi). Signatures count only when the
 *    language already matched at least one word — ä ö å é è are shared by
 *    several languages and must never decide alone. Detection is
 *    precision-first: at least 3 marker hits, 30% of tokens, a 2-hit margin
 *    over every other candidate, and more markers than English function
 *    words. A borderline text keeps the page language — miscategorising
 *    English would re-voice it wrongly, which is worse than leaving one
 *    borderline foreign phrase untagged. Languages without usable markers
 *    (ca, gl, ms, …) stay untagged; written Danish/Norwegian are so close
 *    that ambiguous text deliberately matches neither.
 *  - An element whose OWN lang contradicts its text at the ALPHABET level
 *    (e.g. lang="en" stamped by an app over a Cyrillic review body) is
 *    re-tagged to the detected language — a wrong declaration is worse than
 *    none, and screen readers honour the innermost lang. The pre-existing
 *    value is preserved in data-lang-autotag-original. Cross-alphabet
 *    detection is reliable; same-script disagreements (uk stamped over ru,
 *    where telling them apart needs word statistics) are left alone, and
 *    <html> itself is never touched.
 *  - Live content is handled through a MutationObserver (debounced via
 *    requestAnimationFrame plus a timeout fallback for hidden tabs), so
 *    reviews rendered after page load, pagination, "read more" expansions
 *    and filter changes are tagged as they appear. Shadow roots are picked
 *    up as they are discovered.
 *  - Opt-out: put data-lang-autotag="off" on any subtree that must never be
 *    touched (the attribute value "auto" is this script's own marker).
 *  - Manual re-scan hook for QA / merchant tooling:
 *    window.petkitA11yAutoLang.scan()
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/language-of-page.html
 * @see https://www.w3.org/WAI/WCAG22/Understanding/language-of-parts.html
 * @see https://www.w3.org/TR/WCAG20-TECHS/F71.html
 */
(function () {
  "use strict";

  if (window.petkitA11yAutoLang) return; // guard against double inclusion

  var MARKER_ATTR = "data-lang-autotag"; // "auto" = tagged here, "off" = opt-out
  var ORIGINAL_ATTR = "data-lang-autotag-original"; // lang value found here before re-tagging
  var MIN_FOREIGN_LETTERS = 2; // a single stray letter is not a language signal
  var RUN_FALLBACK_MS = 200; // rAF never fires in hidden tabs

  /* [start, end, script] sorted by code point. Letters only — punctuation,
     digits, combining marks and symbols are not listed, so they are simply
     ignored by the counters. */
  var SCRIPT_RANGES = [
    [0x0041, 0x005a, "latin"],
    [0x0061, 0x007a, "latin"],
    [0x00aa, 0x00aa, "latin"],
    [0x00ba, 0x00ba, "latin"],
    [0x00c0, 0x00d6, "latin"],
    [0x00d8, 0x00f6, "latin"],
    [0x00f8, 0x024f, "latin"],
    [0x0370, 0x03ff, "greek"],
    [0x0400, 0x052f, "cyrillic"],
    [0x0531, 0x058f, "armenian"],
    [0x0591, 0x05f4, "hebrew"],
    [0x0600, 0x06ff, "arabic"],
    [0x0750, 0x077f, "arabic"],
    [0x0900, 0x097f, "devanagari"],
    [0x0980, 0x09ff, "bengali"],
    [0x0a00, 0x0a7f, "gurmukhi"],
    [0x0a80, 0x0aff, "gujarati"],
    [0x0b80, 0x0bff, "tamil"],
    [0x0c00, 0x0c7f, "telugu"],
    [0x0c80, 0x0cff, "kannada"],
    [0x0d00, 0x0d7f, "malayalam"],
    [0x0d80, 0x0dff, "sinhala"],
    [0x0e00, 0x0e7f, "thai"],
    [0x0e80, 0x0eff, "lao"],
    [0x0f00, 0x0fff, "tibetan"],
    [0x1000, 0x109f, "myanmar"],
    [0x10a0, 0x10ff, "georgian"],
    [0x1780, 0x17ff, "khmer"],
    [0x1e00, 0x1eff, "latin"],
    [0x2c60, 0x2c7f, "latin"],
    [0x2d00, 0x2d2f, "georgian"],
    [0x3041, 0x30ff, "kana"],
    [0x3130, 0x318f, "hangul"],
    [0x31f0, 0x31ff, "kana"],
    [0x3400, 0x4dbf, "han"],
    [0x4e00, 0x9fff, "han"],
    [0xa720, 0xa7ff, "latin"],
    [0xa960, 0xa97f, "hangul"],
    [0xac00, 0xd7ff, "hangul"],
    [0xf900, 0xfaff, "han"],
    [0xfb00, 0xfb17, "latin"],
    [0xfb50, 0xfdff, "arabic"],
    [0xfe70, 0xfeff, "arabic"],
    [0xff21, 0xff3a, "latin"],
    [0xff41, 0xff5a, "latin"],
    [0xff66, 0xff9f, "kana"],
    [0x20000, 0x2fa1f, "han"]
  ];

  var SCRIPT_LANGUAGE = {
    greek: "el", cyrillic: "ru", armenian: "hy", hebrew: "he", arabic: "ar",
    devanagari: "hi", bengali: "bn", gurmukhi: "pa", gujarati: "gu",
    tamil: "ta", telugu: "te", kannada: "kn", malayalam: "ml", sinhala: "si",
    thai: "th", lao: "lo", tibetan: "bo", myanmar: "my", georgian: "ka",
    khmer: "km", hangul: "ko", kana: "ja", han: "zh"
  };

  /* Primary subtag of <html lang> → script; subtags not listed are treated
     as Latin-script (the store's default). Only used to know which script
     the page already declares, so inherited text is never re-tagged. */
  var PAGE_LANG_SCRIPT = {
    ru: "cyrillic", uk: "cyrillic", be: "cyrillic", bg: "cyrillic",
    mk: "cyrillic", sr: "cyrillic", el: "greek", he: "hebrew", yi: "hebrew",
    ar: "arabic", fa: "arabic", ur: "arabic", ps: "arabic", hy: "armenian",
    ka: "georgian", hi: "devanagari", bn: "bengali", pa: "gurmukhi",
    gu: "gujarati", ta: "tamil", te: "telugu", kn: "kannada", ml: "malayalam",
    si: "sinhala", th: "thai", lo: "lao", bo: "tibetan", my: "myanmar",
    km: "khmer", ja: "han", ko: "hangul", zh: "han"
  };

  /* Elements whose text is code or form data — re-tagging those would be
     wrong (code samples legitimately contain foreign strings). */
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, CODE: 1, PRE: 1, KBD: 1,
    SAMP: 1, VAR: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1, OPTION: 1, MATH: 1,
    SVG: 1, TITLE: 1
  };

  function scriptOf(codePoint) {
    var lo = 0, hi = SCRIPT_RANGES.length - 1;
    if (codePoint < SCRIPT_RANGES[0][0] || codePoint > SCRIPT_RANGES[hi][1]) {
      return null;
    }
    while (lo <= hi) {
      var mid = (lo + hi) >> 1, range = SCRIPT_RANGES[mid];
      if (codePoint < range[0]) hi = mid - 1;
      else if (codePoint > range[1]) lo = mid + 1;
      else return range[2];
    }
    return null;
  }

  /* ES5-safe code point iteration (handles surrogate pairs for CJK ext B+). */
  function eachCodePoint(text, fn) {
    var i = 0, len = text.length;
    while (i < len) {
      var cp = text.charCodeAt(i);
      if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < len) {
        var low = text.charCodeAt(i + 1);
        if (low >= 0xdc00 && low <= 0xdfff) {
          cp = 0x10000 + ((cp - 0xd800) << 10) + (low - 0xdc00);
          i++;
        }
      }
      fn(cp);
      i++;
    }
  }

  function refineCyrillic(text) {
    if (text.indexOf("ў") !== -1) return "be";
    if (text.indexOf("ї") !== -1 || text.indexOf("ґ") !== -1 ||
        text.indexOf("є") !== -1 || text.indexOf("і") !== -1) return "uk";
    if (text.indexOf("ђ") !== -1 || text.indexOf("ћ") !== -1 ||
        text.indexOf("џ") !== -1 || text.indexOf("љ") !== -1 ||
        text.indexOf("њ") !== -1) return "sr";
    if (text.indexOf("ѓ") !== -1 || text.indexOf("ќ") !== -1) return "mk";
    return "ru"; // ru and bg share the alphabet; telling them apart needs
                 // word-level statistics, so the common default is used
  }

  /* ---- Same-alphabet (Latin) language detection -------------------------
     Unicode scripts cannot separate English from French or Spanish — they
     all share the alphabet. These lists hold high-frequency FUNCTION words
     that are either unique to a language or shared in ways that do not
     discriminate (a word in two lists scores for both, so it never decides
     between them). Words excluded on purpose: anything that is also a
     common English word used in a different role (en "do/was/hat/per/on/
     was", de "was/hat/war"), and single-letter articles (en "a/i", it "i/e")
     which would flood the counters. Precision beats recall: a text below
     the gates stays in the page language. */
  var LATIN_WORDS = {
    es: ["el", "la", "los", "las", "de", "del", "que", "y", "en", "un",
         "una", "es", "son", "esta", "este", "esto", "estos", "estas",
         "está", "están", "muy", "más", "pero", "como", "para", "con",
         "por", "sin", "sobre", "todo", "toda", "cuando", "donde",
         "porque", "también", "gracias", "bien", "mi", "tu", "su", "sus",
         "le", "les", "lo", "se", "yo", "ella", "ellos", "ha", "han",
         "hay", "fue", "día", "me", "no", "ya", "ni", "o", "sí",
         "buena", "bueno", "mucho", "mucha", "recomiendo", "recomendable",
         "producto", "gato", "gatos", "perro", "perros", "comida",
         "calidad", "perfecto", "perfecta", "excelente", "come"],
    fr: ["le", "la", "les", "de", "des", "du", "un", "une", "et", "est",
         "sont", "je", "tu", "il", "elle", "ils", "elles", "nous", "vous",
         "ce", "cet", "cette", "ces", "ça", "mon", "ma", "mes", "ton",
         "ta", "tes", "son", "sa", "ses", "notre", "votre", "leur",
         "leurs", "y", "en", "dans", "sur", "pour", "par", "avec",
         "sans", "plus", "très", "moins", "bien", "bon", "bonne", "qui",
         "que", "quoi", "ne", "pas", "mais", "aussi", "tout", "tous",
         "comme", "merci", "chien", "chat", "produit", "qualité",
         "vraiment", "ai", "as", "ont", "avons", "avez", "être",
         "était", "chez", "alors", "chose", "animaux", "croquettes"],
    pt: ["não", "você", "da", "do", "das", "dos", "dele", "dela", "meu",
         "minha", "meus", "minhas", "isso", "esse", "essa", "este",
         "esta", "isto", "muito", "bem", "bom", "boa", "já", "então",
         "também", "mas", "mais", "como", "para", "em", "numa", "num",
         "pelo", "pela", "os", "as", "um", "uma", "uns", "umas", "com",
         "que", "se", "seu", "sua", "está", "estão", "são", "era", "foi",
         "ter", "tem", "tenho", "pra", "pro", "gato", "gata", "cachorro",
         "produto", "qualidade", "recomendo", "ótimo", "ótima", "tudo"],
    it: ["il", "lo", "la", "gli", "le", "un", "uno", "una", "di", "del",
         "della", "che", "non", "sono", "è", "ho", "hai", "hanno", "mia",
         "mio", "tuo", "tua", "suo", "sua", "questo", "questa", "questi",
         "queste", "quello", "quella", "molto", "bene", "buona", "buono",
         "anche", "più", "come", "ma", "perché", "quando", "dove",
         "senza", "grazie", "gatto", "gatti", "cane", "cani", "prodotto",
         "qualità", "consiglio", "consigliato", "tutto", "cibo"],
    de: ["der", "die", "das", "und", "ist", "nicht", "mit", "ich", "sie",
         "er", "ein", "eine", "einen", "einem", "einer", "sehr", "gut",
         "gute", "aber", "für", "auf", "von", "zu", "im", "am", "dem",
         "den", "des", "auch", "noch", "schon", "nur", "wie", "habe",
         "haben", "bin", "waren", "werde", "werden", "würde", "kann",
         "können", "muss", "müssen", "bei", "nach", "aus", "über",
         "unter", "vor", "durch", "um", "ohne", "zum", "zur", "mein",
         "meine", "dein", "sein", "ihre", "uns", "mich", "dich", "mir",
         "dir", "sich", "danke", "katze", "katzen", "hund", "hunde",
         "produkt", "qualität", "futter", "wirklich", "toll"],
    nl: ["het", "een", "van", "niet", "met", "voor", "mijn", "jouw",
         "zijn", "heeft", "hebben", "geen", "heel", "veel", "goed",
         "goede", "prima", "deze", "dit", "wel", "nog", "onze", "zeker",
         "aanraden", "hond", "kat", "product"],
    sv: ["och", "att", "det", "som", "på", "för", "med", "den", "till",
         "är", "inte", "har", "hade", "hon", "han", "min", "mitt",
         "denna", "detta", "mycket", "bra", "katt", "hund", "produkt",
         "kvalitet", "rekommenderar", "mat", "maten", "köpte"],
    da: ["og", "ikke", "af", "til", "den", "det", "med", "min", "mit",
         "god", "godt", "gode", "meget", "denne", "hund", "kat",
         "produkt", "kvalitet", "anbefaler", "anbefales", "også",
         "helt", "vores", "foder", "hunde", "katte", "jeg"],
    no: ["og", "ikke", "den", "det", "med", "min", "mitt", "god",
         "godt", "meget", "denne", "hund", "katt", "produkt",
         "kvalitet", "anbefaler", "også", "veldig", "gir", "vår",
         "etter", "mat", "hunder", "katter", "fôr", "jeg"],
    fi: ["ja", "ei", "että", "sen", "hän", "minun", "sinun", "kaikki",
         "paljon", "hyvä", "hyvin", "kissa", "koira", "tuote", "laatu",
         "suosittelen", "ostin", "oli", "ovat", "tämä", "tätä", "kiitos",
         "ruoka", "kissan", "koiran"],
    pl: ["nie", "jest", "że", "dla", "jako", "bardzo", "dobry", "dobra",
         "dobre", "kot", "koty", "pies", "psa", "produkt", "jakość",
         "polecam", "ten", "tego", "tej", "mnie", "jestem", "mam",
         "miał", "zakupu", "karma", "karmy", "miska", "są", "tylko",
         "dziękuję", "kupiłam", "kupiłem"],
    cs: ["je", "není", "která", "které", "který", "jsem", "jsme",
         "velmi", "dobrá", "dobrý", "dobré", "kočka", "kočky", "pes",
         "produkt", "kvalita", "doporučuji", "tato", "tento", "toho",
         "mě", "koupila", "koupil", "krmení", "spokojená", "spokojený"],
    sk: ["je", "som", "si", "veľmi", "veľký", "mačka", "mačky", "pes",
         "kvalita", "odporúčam", "tento", "toto", "nie", "dobrý",
         "dobrá", "kúpila", "kúpil", "krmivo", "jedlo", "produkty"],
    hu: ["nem", "és", "egy", "az", "hogy", "van", "vannak", "nagyon",
         "jó", "macska", "macskám", "kutya", "kutyám", "termék",
         "minőség", "minőségű", "ajánlom", "nekem", "neki", "köszönöm",
         "tápot", "eledel"],
    ro: ["și", "este", "sunt", "foarte", "bun", "bună", "buna",
         "pisica", "pisici", "câine", "câini", "caine", "produs",
         "calitate", "recomand", "acest", "acesta", "această", "pentru",
         "mai", "din", "dar", "care", "mult", "cumpărat", "hrana",
         "motan"],
    tr: ["bir", "ve", "bu", "çok", "ama", "için", "daha", "kedi",
         "kedim", "köpek", "ürün", "kaliteli", "tavsiye", "ederim",
         "benim", "oldukça", "güzel", "seviyorum", "aldım",
         "kullanıyorum", "mama", "maması", "hayvanım"],
    id: ["yang", "dan", "itu", "ini", "dengan", "untuk", "tidak",
         "sangat", "bagus", "sekali", "sudah", "akan", "saja", "juga",
         "kucing", "saya", "kami", "bisa", "banget", "produk",
         "kualitas", "rekomendasi", "beli", "suka"],
    vi: ["của", "không", "rất", "này", "mình", "món", "tốt", "mèo",
         "chó", "sản", "phẩm", "chất", "lượng", "được", "đã", "dễ",
         "sạch", "mua", "dùng", "nên"],
    tl: ["ang", "mga", "ako", "yung", "naman", "talaga", "hindi",
         "sobra", "salamat", "rin", "din", "yun", "kasi", "kayo",
         "pusa", "aso", "produkto", "sulit", "gusto", "mabuti",
         "maganda"]
  };
  var ENGLISH_WORDS = ["the", "a", "an", "and", "or", "but", "if", "of",
    "to", "in", "on", "at", "by", "for", "with", "from", "as", "is",
    "are", "was", "were", "be", "been", "being", "do", "does", "did",
    "have", "has", "had", "i", "you", "your", "yours", "he", "she", "it",
    "its", "we", "they", "them", "their", "our", "us", "my", "me",
    "mine", "this", "that", "these", "those", "there", "here", "when",
    "where", "why", "how", "what", "which", "who", "will", "would",
    "can", "could", "should", "may", "might", "must", "not", "no",
    "yes", "very", "really", "just", "only", "about", "after", "before",
    "because", "than", "then", "so", "too", "also", "more", "most",
    "much", "many", "some", "any", "all", "both", "each", "every",
    "other", "another", "same", "own", "out", "up", "down", "over",
    "under", "again", "once", "while", "until", "during", "without",
    "within", "into", "off", "per", "plus", "well", "get", "got", "one",
    "two", "good", "great", "love", "loves", "loved", "like", "likes",
    "work", "works", "working", "buy", "bought", "use", "used", "using",
    "cat", "cats", "dog", "dogs", "product", "quality", "little",
    "her", "hers", "ten", "mat"];

  var LATIN_WORD_LOOKUP = {};
  var ENGLISH_WORD_LOOKUP = {};
  (function buildWordLookups() {
    function toLookup(words) {
      var lookup = {};
      for (var i = 0; i < words.length; i++) lookup[words[i]] = 1;
      return lookup;
    }
    for (var lang in LATIN_WORDS) LATIN_WORD_LOOKUP[lang] = toLookup(LATIN_WORDS[lang]);
    ENGLISH_WORD_LOOKUP = toLookup(ENGLISH_WORDS);
  })();

  /* Letters that are near-unique to one Latin-script language; each
     occurrence counts as two word hits (a single ñ already makes Spanish
     very likely). Shared diacritics are excluded on purpose: ä ö å (sv da
     no fi et), é è ê à ç (fr pt it), ă (vi) — they would misattribute
     Finnish or Vietnamese text to a wrong language. Signatures only count
     when the language already matched at least one word (see scoring). */
  var SIG_CHARS = {};
  (function buildSigChars() {
    var groups = { es: "ñ¿¡", pt: "ãõ", de: "ßü", pl: "ąćęłńśźż",
                   tr: "ğşı", ro: "șțţ", cs: "řůě", sk: "ľĺ",
                   hu: "őű",
                   vi: "ơưđạảấầẩẫậắằẳẵặẹẻẽếềểễệỉịọỏốồổỗộớờởỡợụủứừửữựỳỵỷỹ" };
    for (var lang in groups) {
      var chars = groups[lang];
      for (var i = 0; i < chars.length; i++) SIG_CHARS[chars.charAt(i)] = lang;
    }
  })();

  var LATIN_MIN_TOKENS = 6;    // fewer words carry no reliable signal
  var LATIN_MIN_SCORE = 3;     // one stray "de" or "el" proves nothing
  var LATIN_MIN_DENSITY = 0.3; // markers per token — diluted text stays put
  var LATIN_MARGIN = 2;        // must clearly beat every sibling language
  var SIG_WEIGHT = 2;

  function detectLatinLanguage(text) {
    var tokens = [];
    var sigCounts = {};
    var current = "";
    function flush() {
      if (current) { tokens.push(current); current = ""; }
    }
    eachCodePoint(text, function (cp) {
      if (scriptOf(cp) === "latin") {
        var ch = String.fromCharCode(cp).toLowerCase();
        var sig = SIG_CHARS[ch];
        if (sig) sigCounts[sig] = (sigCounts[sig] || 0) + 1;
        current += ch;
      } else {
        flush();
      }
    });
    flush();

    if (tokens.length < LATIN_MIN_TOKENS) return null;

    var best = null, bestScore = 0, second = 0;
    for (var lang in LATIN_WORD_LOOKUP) {
      var words = LATIN_WORD_LOOKUP[lang];
      var wordHits = 0;
      for (var i = 0; i < tokens.length; i++) {
        if (words[tokens[i]]) wordHits++;
      }
      /* Signature letters only count for a language that also matched at
         least one word: ä ö å é are shared across several languages and
         must never decide on their own (Finnish ä would otherwise read as
         German). */
      var score = wordHits + (wordHits ? (sigCounts[lang] || 0) * SIG_WEIGHT : 0);
      if (score > bestScore) { second = bestScore; bestScore = score; best = lang; }
      else if (score > second) second = score;
    }

    if (!best || bestScore < LATIN_MIN_SCORE) return null;
    if (bestScore - second < LATIN_MARGIN) return null;
    if (bestScore / tokens.length < LATIN_MIN_DENSITY) return null;

    var english = 0;
    for (var j = 0; j < tokens.length; j++) {
      if (ENGLISH_WORD_LOOKUP[tokens[j]]) english++;
    }
    if (english >= bestScore) return null;

    return best;
  }

  /**
   * @return best BCP 47 tag for this text, or null when it should keep the
   *         page language (mixed snippets, too few letters, no confident
   *         Latin-language match …).
   */
  function detectLanguage(text, pageScript) {
    var counts = {}, total = 0, foreign = 0, latin = 0;

    eachCodePoint(text, function (cp) {
      var script = scriptOf(cp);
      if (!script) return;
      counts[script] = (counts[script] || 0) + 1;
      total++;
      if (script === "latin") latin++;
      else if (script !== pageScript) foreign++;
    });

    // Foreign script must DOMINATE the element's letters — an English
    // sentence quoting one kana word stays in the page language.
    if (foreign >= MIN_FOREIGN_LETTERS && foreign * 2 > total) {
      // Any kana/hangul or a Han presence decides immediately.
      if (counts.kana) return "ja";
      if (counts.hangul) return "ko";
      if (counts.han) return "zh";
      var best = null, bestCount = 0;
      for (var script in counts) {
        if (script === "latin" || script === pageScript) continue;
        if (counts[script] > bestCount) { bestCount = counts[script]; best = script; }
      }
      if (best) {
        return best === "cyrillic" ? refineCyrillic(text) : (SCRIPT_LANGUAGE[best] || null);
      }
    }

    // Predominantly Latin letters: the alphabet is shared with the page,
    // so language comes from word statistics (or stays the page language).
    if (latin * 2 > total) return detectLatinLanguage(text);

    return null;
  }

  function primarySubtag(langValue) {
    if (!langValue) return null;
    var match = /^\s*([a-zA-Z]+)/.exec(langValue);
    return match ? match[1].toLowerCase() : null;
  }

  /* Only text nodes that are direct children count, so tagging happens at
     the most specific element and mixed-in child elements keep their own
     chances to be classified. */
  function ownText(el) {
    var parts = [];
    for (var node = el.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === 3) parts.push(node.data);
    }
    return parts.join(" ");
  }

  /* Nearest ANCESTOR (the element's own lang is judged separately) already
     declaring the same language → the tag would be redundant (this also
     short-circuits children of tagged elements and elements inheriting
     <html lang> when it already matches). */
  function hasRedundantAncestorLang(el, tag) {
    var start = el.parentElement;
    var ancestor = start && start.closest ? start.closest("[lang]") : null;
    return !!ancestor && primarySubtag(ancestor.getAttribute("lang")) === tag;
  }

  function isOptedOut(el) {
    return !!el.closest('[' + MARKER_ATTR + '="off"]');
  }

  var observedRoots = [];
  var observer = typeof MutationObserver !== "undefined"
    ? new MutationObserver(function () { schedule(); })
    : null;

  function observeRoot(root) {
    if (!observer) return;
    for (var i = 0; i < observedRoots.length; i++) {
      if (observedRoots[i] === root) return;
    }
    observedRoots.push(root);
    observer.observe(root, { childList: true, characterData: true, subtree: true });
  }

  function scanRoot(root, pagePrimary, pageScript) {
    var elements = root.querySelectorAll("*");
    var shadowRoots = [];

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (SKIP_TAGS[el.tagName]) continue;
      if (el.shadowRoot) shadowRoots.push(el.shadowRoot);
      if (el === document.documentElement || el.isContentEditable) continue;

      var text = ownText(el);
      if (!text) continue;

      var tag = detectLanguage(text, pageScript);
      if (!tag || tag === pagePrimary) continue;
      if (isOptedOut(el)) continue;

      /* The element's own lang: keep it when it already names the detected
         language, and keep it too when the disagreement is inside one
         non-Latin alphabet (uk vs ru needs word statistics we don't have).
         Everything else contradicts the text — an app-stamped lang="en"
         over a Cyrillic or Spanish body is exactly what the scan flags —
         and Latin-vs-Latin (en over es) IS word-level decidable, so those
         get corrected to the detected language. */
      var existing = el.getAttribute("lang");
      var existingPrimary = primarySubtag(existing);
      if (existingPrimary === tag) continue;
      /* A correction happens even when an ancestor already declares the
         detected language: screen readers honour the innermost
         declaration, so leaving lang="en" in place would keep
         mispronouncing the text. */
      if (existingPrimary) {
        var existingScript = PAGE_LANG_SCRIPT[existingPrimary];
        var tagScript = PAGE_LANG_SCRIPT[tag];
        if (existingScript && existingScript === tagScript) continue;
      } else if (hasRedundantAncestorLang(el, tag)) {
        continue;
      }

      if (existing && el.getAttribute(MARKER_ATTR) !== "auto" && !el.hasAttribute(ORIGINAL_ATTR)) {
        el.setAttribute(ORIGINAL_ATTR, existing);
      }
      el.setAttribute("lang", tag);
      el.setAttribute(MARKER_ATTR, "auto");
    }

    for (var j = 0; j < shadowRoots.length; j++) {
      observeRoot(shadowRoots[j]);
      scanRoot(shadowRoots[j], pagePrimary, pageScript);
    }
  }

  function runPass() {
    var root = document.body || document.documentElement;
    if (!root) return;
    var pageLang = (document.documentElement && document.documentElement.getAttribute("lang")) || "";
    var pagePrimary = primarySubtag(pageLang);
    var pageScript = (pagePrimary && PAGE_LANG_SCRIPT[pagePrimary]) || "latin";
    scanRoot(root, pagePrimary, pageScript);
  }

  /* Debounce DOM bursts (widget re-renders) into one pass per frame; the
     timeout keeps the script working in hidden tabs where rAF stalls. */
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    var fire = function () {
      if (!scheduled) return;
      scheduled = false;
      runPass();
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(fire);
    window.setTimeout(fire, RUN_FALLBACK_MS);
  }

  observeRoot(document.documentElement);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runPass);
  } else {
    runPass();
  }

  window.petkitA11yAutoLang = { scan: runPass };
})();
